import { GeneGrouping, GeneGroupsByGrouping, L2Target, L2Targets } from '@resistapp/common/assays';
import {
  AllProjectSampleTypesGroup,
  ComparableSampleGroupType,
  SampleTypeGroup,
} from '@resistapp/common/comparable-sample-groups';
import { SampleType } from '@resistapp/common/sampling-point-types';
import { NormalisationMode, XAxisMode } from '@resistapp/common/types';
import { values } from 'lodash';
import { DEFAULT_GENE_GROUPING } from '../data-utils/gene-groups';

export enum QueryParams {
  GENE_GROUP_GROUPING = 'ggrouping',
  GENE_GROUPS = 'ggroups',
  SAMPLE_TYPE = 'stype',
  SAMPLING_POINTS = 'samplingPoints',
  CHIPS = 'chips',
  NORMALISATION_MODE = 'normalisationMode', // Previously called absoluteMode
  START = 'start',
  END = 'end',
  X_AXIS_MODE = 'xAxis',
}

export enum LegacyQueryParams {
  ENVIRONMENT_TYPE = 'etype',
  ENVIRONMENTS = 'environments',
}

export enum OtherParams {
  COUNTRY = 'country',
  VERIFICATION_CODE = 'code',
  EXPERIMENTAL = 'experimental',
}

export function getGeneGroupingParams(searchParams: URLSearchParams, allGeneGroups: GeneGroupsByGrouping) {
  const geneGroupParam = searchParams.get(QueryParams.GENE_GROUP_GROUPING);

  return isGeneGrouping(allGeneGroups, geneGroupParam) ? geneGroupParam : DEFAULT_GENE_GROUPING;
}

export function getGeneGroupsParams(searchParams: URLSearchParams | string | null) {
  const geneGroupsParam = typeof searchParams === 'string' ? searchParams : searchParams?.get(QueryParams.GENE_GROUPS);
  const plainGeneGroupsParam = splitQueryParamArray(geneGroupsParam, true) ?? [];
  // Backwards compatibility
  return plainGeneGroupsParam.filter(g => !!g).map(translateLegacyTarget);
}

export function getSamplingPointTypeParam(searchParams: URLSearchParams): SampleTypeGroup | undefined {
  const newParam = searchParams.get(QueryParams.SAMPLE_TYPE);
  const legacyParam = searchParams.get(LegacyQueryParams.ENVIRONMENT_TYPE);
  const plainParam = newParam ?? legacyParam;

  const samplingPointTypesParam = normalizeSamplingPointTypeParam(plainParam);

  if (!samplingPointTypesParam) {
    return undefined;
  }

  const samplingPointTypes = splitQueryParamArray(samplingPointTypesParam) as SampleTypeGroup[];
  const validSamplingPointTypes = samplingPointTypes.filter(type => isComparableGroupOrSampleType(type));

  if (validSamplingPointTypes.length > 1) {
    console.error('Unexpectedly got multiple sample types in query params, reverting to one', validSamplingPointTypes);
  }

  const selectedType = validSamplingPointTypes[0];

  if (legacyParam && validSamplingPointTypes.length) {
    const joinedTypes = joinQueryParamArray([selectedType]);
    searchParams.set(QueryParams.SAMPLE_TYPE, joinedTypes);
    searchParams.delete(LegacyQueryParams.ENVIRONMENT_TYPE);
  }

  return selectedType;
}

export function getSamplingPointsParams(
  searchParams: URLSearchParams | string | null,
  samplingPointNamesHaveComma: boolean,
) {
  const isSearchParamsObject = typeof searchParams !== 'string' && searchParams !== null;
  const searchParamsObject = isSearchParamsObject ? searchParams : undefined;
  const newParamText =
    typeof searchParams === 'string'
      ? searchParams
      : searchParamsObject
        ? searchParamsObject.get(QueryParams.SAMPLING_POINTS)
        : undefined;
  const legacyParamText = searchParamsObject ? searchParamsObject.get(LegacyQueryParams.ENVIRONMENTS) : undefined;
  const resolvedParamText = newParamText ?? legacyParamText;

  const samplingPointsParam = splitQueryParamArray(resolvedParamText, !samplingPointNamesHaveComma) ?? [];
  const samplingPointIds = samplingPointsParam
    .filter(id => !!id)
    .map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));

  if (legacyParamText && searchParamsObject) {
    const joinedIds = samplingPointIds.length ? joinQueryParamArray(samplingPointIds.map(String)) : '';
    if (joinedIds) {
      searchParamsObject.set(QueryParams.SAMPLING_POINTS, joinedIds);
    } else {
      searchParamsObject.delete(QueryParams.SAMPLING_POINTS);
    }
    searchParamsObject.delete(LegacyQueryParams.ENVIRONMENTS);
  }

  return samplingPointIds;
}

export function getChipsParams(searchParams: URLSearchParams | string | null) {
  const chipsParamText = typeof searchParams === 'string' ? searchParams : searchParams?.get(QueryParams.CHIPS);
  const chipsParam = splitQueryParamArray(chipsParamText) ?? [];
  return chipsParam
    .filter(id => !!id)
    .map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));
}

export function getXAxisModeParam(searchParams: URLSearchParams): XAxisMode | undefined {
  const xAxisMode = searchParams.get(QueryParams.X_AXIS_MODE) as XAxisMode | null;
  return xAxisMode && values(XAxisMode).includes(xAxisMode) ? xAxisMode : undefined;
}

export function getNormalisationModeParam(searchParams: URLSearchParams): NormalisationMode {
  let mutatingNormalisationModeParam = searchParams.get(QueryParams.NORMALISATION_MODE);
  if (!mutatingNormalisationModeParam) {
    const legacyName = 'absoluteMode';
    const legacyAbsoluteModeParam = searchParams.get(legacyName);
    // Convert legacy boolean to new mode
    const newMode =
      legacyAbsoluteModeParam === 'true' ? NormalisationMode.TEN_UL_DILUTED_DNA_16S : NormalisationMode.SIXTEEN_S;
    searchParams.set(QueryParams.NORMALISATION_MODE, newMode);
    mutatingNormalisationModeParam = newMode;
    searchParams.delete(legacyName);
  }

  // Convert legacy enum values to current values.
  if (mutatingNormalisationModeParam === 'LITRE') {
    mutatingNormalisationModeParam = NormalisationMode.LITRE_16S;
    searchParams.set(QueryParams.NORMALISATION_MODE, mutatingNormalisationModeParam);
  } else if (mutatingNormalisationModeParam === 'TEN_UL_DILUTED_DNA') {
    mutatingNormalisationModeParam = NormalisationMode.TEN_UL_DILUTED_DNA_16S;
    searchParams.set(QueryParams.NORMALISATION_MODE, mutatingNormalisationModeParam);
  }

  // Validate that the mode is a valid NormalisationMode
  return values(NormalisationMode).includes(mutatingNormalisationModeParam as NormalisationMode)
    ? (mutatingNormalisationModeParam as NormalisationMode)
    : NormalisationMode.SIXTEEN_S;
}

function normalizeSamplingPointTypeParam(param?: string | null) {
  if (!param) {
    return undefined;
  }

  const parts = splitQueryParamArray(param) ?? [param];
  const normalizedParts = parts
    .map(normalizeSingleSamplingPointType)
    .filter((value): value is SampleTypeGroup => Boolean(value));

  if (!normalizedParts.length) {
    return undefined;
  }

  return joinQueryParamArray(normalizedParts);
}

function normalizeSingleSamplingPointType(type?: string | null): SampleTypeGroup | undefined {
  if (!type) {
    return undefined;
  }

  if (type === 'Waste water' || type === 'Waste+water' || type === 'Waste%20water') {
    return SampleType.WASTEWATER;
  }

  if (type === 'Surface water' || type === 'Surface+water' || type === 'Surface%20water' || type === 'NATURAL_WATER') {
    return SampleType.SURFACE_WATER;
  }

  const upperCased = type.toUpperCase();
  return isComparableGroupOrSampleType(upperCased) ? upperCased : undefined;
}

function isGeneGrouping(allGeneGroups: GeneGroupsByGrouping, param?: string | null): param is GeneGrouping {
  return Boolean(param && param in allGeneGroups);
}

function isComparableGroupOrSampleType(param?: string | null): param is SampleTypeGroup {
  return Boolean(
    param &&
      (values(SampleType as Record<string, string>).includes(param) ||
        values(ComparableSampleGroupType as Record<string, string>).includes(param)),
  );
}

export function mutateSamplingPointRelatedSearchParams(
  searchParams: URLSearchParams,
  group: SampleTypeGroup,
  clearSmplPnts: boolean,
) {
  if (clearSmplPnts) {
    searchParams.delete(QueryParams.SAMPLING_POINTS);
    searchParams.delete(LegacyQueryParams.ENVIRONMENTS);
  }
  if (group === AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS) {
    searchParams.delete(QueryParams.SAMPLE_TYPE);
    searchParams.delete(LegacyQueryParams.ENVIRONMENT_TYPE);
  } else {
    // Note: we supported selecting multiple smplPnt types briefly in the past,
    // but to the best of my recollection the functionality was never really used
    const joinedTypes = joinQueryParamArray([group]);
    searchParams.set(QueryParams.SAMPLE_TYPE, joinedTypes);
    searchParams.delete(LegacyQueryParams.ENVIRONMENT_TYPE);
  }
}

export function mutateSamplingPointScopeSearchParams(
  searchParams: URLSearchParams,
  group: SampleTypeGroup,
  wholeGroupSamplingPointIds: number[],
  selectedSamplingPointIds?: number[],
) {
  const hasExplicitSamplingPointScope = Boolean(selectedSamplingPointIds?.length);
  mutateSamplingPointRelatedSearchParams(searchParams, group, !hasExplicitSamplingPointScope);

  if (!hasExplicitSamplingPointScope) {
    return;
  }

  mutateSearchParamsWithSelection(
    QueryParams.SAMPLING_POINTS,
    searchParams,
    wholeGroupSamplingPointIds,
    selectedSamplingPointIds ?? [],
    true,
  );
}

export function mutateSearchParamsWithSelection(
  paramID: string,
  searchParams: URLSearchParams,
  wholeGroup: string[] | number[], // Accept numbers
  next: string[] | number[], // Accept numbers
  only = false,
) {
  // If the user wants to select only one value, we remove the old selections
  if (only) {
    searchParams.delete(paramID);
  }

  // If the next group is the same as all groups, we don't add ALL the groups to the
  // URL, but instead empty URL === default to all groups selected
  // Stringify numbers before comparison and joining
  const stringifiedNext = next.map(String);
  const stringifiedWholeGroup = wholeGroup.map(String);
  const selectedSamplingPoints =
    // Preserve selection order, don't sort original stringifiedNext
    JSON.stringify([...stringifiedNext].sort()) !== JSON.stringify(stringifiedWholeGroup.sort())
      ? joinQueryParamArray(stringifiedNext)
      : '';

  if (!selectedSamplingPoints) {
    searchParams.delete(paramID);
  } else {
    searchParams.set(paramID, selectedSamplingPoints);
  }
}

// Note: changing this will break old URLs
// Split query param arrays with a human readable string unlikely to be found (though not forbidden) in smplPnt names
export const queryParamArraySeparator = '_-.-_';
export function splitQueryParamArray(param?: string | null, allowLegacyCommaSeparator?: boolean) {
  if (param && allowLegacyCommaSeparator && !param.includes(queryParamArraySeparator) && param.includes(',')) {
    return param.split(',');
  }
  return param?.split(queryParamArraySeparator);
}

export function joinQueryParamArray(param: string[]) {
  return param.join(queryParamArraySeparator);
}

export function translateLegacyTarget(target: string): L2Target {
  switch (target) {
    case 'Beta Lactam':
    case 'Beta+Lactam':
    case 'Beta%20Lactam':
    case 'Beta-Lactam':
    case 'BETA_LACTAM':
      return L2Targets.BETA_LACTAM;

    case 'MGE':
      return L2Targets.MGE;

    case 'Integrons':
    case 'INTEGRONS':
      return L2Targets.INTEGRONS;

    case 'Vancomycin':
    case 'VANCOMYCIN':
      return L2Targets.VANCOMYCIN;

    case 'MDR':
      return L2Targets.MDR;

    case 'Trimethoprim':
    case 'TRIMETHOPRIM':
      return L2Targets.TRIMETHOPRIM;

    case 'Phenicol':
    case 'PHENICOL':
      return L2Targets.PHENICOL;

    case 'Quinolone':
    case 'QUINOLONE':
      return L2Targets.QUINOLONE;

    case 'Sulfonamide':
    case 'SULFONAMIDE':
      return L2Targets.SULFONAMIDE;

    case 'Tetracycline':
    case 'TETRACYCLINE':
      return L2Targets.TETRACYCLINE;

    case 'Aminoglycoside':
    case 'AMINOGLYCOSIDE':
      return L2Targets.AMINOGLYCOSIDE;

    case 'MLSB':
      return L2Targets.MLSB;

    case 'Other':
    case 'OTHER_MICROBIAL_MARKER':
      return L2Targets.OTHER_MICROBIAL_MARKER;

    case 'Taxonomic':
    case 'OTHER_TAXONOMIC_MARKER':
      return L2Targets.OTHER_TAXONOMIC_MARKER;

    case '16S rRNA':
    case 'SIXTEEN_S_RRNA':
      return L2Targets.SIXTEENS_RRNA;

    default:
      return target as L2Target;
  }
}

export function getCountryParam(searchParams: URLSearchParams) {
  return searchParams.get(OtherParams.COUNTRY);
}
